import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@web-chat-core/ui-kit';
import { FEATURES } from 'consts';
import { useUserSettingsContext } from 'contexts';
import { selectLocalVideoTrack } from 'state/selectors/localTracks';
import type { LocalVideoTrack } from 'livekit-client';
import { isFeatureOn } from '../../../../useCase';
import { useManageVideoDevices } from '../../hooks/useManageVideoDevices';

export type WaitingApprovalViewModelParams = {
    isJoinRejected?: boolean;
    onRetry: () => void;
    onDismiss?: () => void;
};

export type WaitingApprovalControlsViewModel = {
    toggleAudio: () => void;
    selectAudioInputDevice: (device: MediaDeviceInfo) => void;
    selectAudioOutputDevice: (device: MediaDeviceInfo) => void;
    toggleVideo: () => void;
    selectVideoDevice: (device: MediaDeviceInfo) => void;
    isPushToTalkEnabled: boolean;
    isMicToggleEnabled: boolean;
};

export type WaitingLayout = 'video' | 'waiting_placeholder' | 'rejected_placeholder';

export type WaitingApprovalViewModel = {
    isJoinRejected: boolean;
    layout: WaitingLayout;
    localVideoTrack: LocalVideoTrack | null | undefined;
    onRetry: () => void;
    onDismiss?: () => void;
    controls: WaitingApprovalControlsViewModel;
    labels: {
        waiting: string;
        declined: string;
        retry: string;
        dismiss: string;
    };
};

export const useWaitingApprovalViewModel = ({
    isJoinRejected = false,
    onRetry,
    onDismiss,
}: WaitingApprovalViewModelParams): WaitingApprovalViewModel => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { audioEnabled, setAudioEnabled, videoEnabled, setAudioInputDeviceId, setAudioOutputDeviceId, permissions } =
        useUserSettingsContext();
    const { toggleVideo, selectVideoDevice } = useManageVideoDevices();
    const localVideoTrack = selectLocalVideoTrack();

    const layout: WaitingLayout = (() => {
        if (!isJoinRejected && videoEnabled && Boolean(localVideoTrack)) return 'video';
        if (isJoinRejected) return 'rejected_placeholder';
        return 'waiting_placeholder';
    })();

    const selectAudioInputDevice = useCallback(
        ({ deviceId }: MediaDeviceInfo) => {
            setAudioInputDeviceId(deviceId);
        },
        [setAudioInputDeviceId],
    );

    const selectAudioOutputDevice = useCallback(
        ({ deviceId }: MediaDeviceInfo) => {
            setAudioOutputDeviceId(deviceId);
        },
        [setAudioOutputDeviceId],
    );

    const toggleAudio = useCallback(() => {
        const next = !audioEnabled;
        if (next && permissions.audio === false) {
            showSnackbar(t('web_call_access_site'), { testId: 'snackbar-mic-access-denied' });
            return;
        }
        setAudioEnabled(next);
    }, [audioEnabled, permissions.audio, setAudioEnabled, showSnackbar, t]);

    return {
        isJoinRejected,
        layout,
        localVideoTrack,
        onRetry,
        onDismiss,
        controls: {
            toggleAudio,
            selectAudioInputDevice,
            selectAudioOutputDevice,
            toggleVideo,
            selectVideoDevice,
            isPushToTalkEnabled: isFeatureOn(FEATURES.VCS_MIC_PUSH_ENABLED),
            isMicToggleEnabled: isFeatureOn(FEATURES.VCS_MIC_TOGGLE_ENABLED),
        },
        labels: {
            waiting: t('vks_request_wait_for_approval'),
            declined: t('vks_request_declined'),
            retry: t('vks_access_limit_reached_retry'),
            dismiss: t('web_videoconference_AI_Assistant_onbording_button'),
        },
    };
};
